import React, { forwardRef, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';

import {
  Input,
  LocationAutoCompleteList,
  useLocationSelection,
  CustomDatePicker,
  VisaTypeList,
  useAuth,
  GenderList,
  VisibilityRadioGroup,
} from '@repo/common';
import KoreanSkillsList from '../../../../../../../packages/common/src/components/List/KoreanSkillsList';
import TopikSpeakingList from '../../../../../../../packages/common/src/components/List/TopikSpeakingList';
import TopikList from '../../../../../../../packages/common/src/components/List/TopikList';
import NationalityInputBlock from '../../../Career/Steps/ProfileStep/PersonalInfo/DynamicFields/NationalityInputBlock';
import RoleList from '../../../JobBoard/Post/JobInfo/RoleList';
import CareerList from '../../../Career/Steps/ProfileStep/PersonalInfo/CareerList';
import PersonalInfoProfileImage from './PersonalInfoProfileImage';

import { ReactComponent as LocationIcon } from '@repo/common/assets/icons/16px/icon_16px_location.svg';
import { ReactComponent as CalendarIcon } from '@repo/common/assets/icons/16px/icon_16px_calendar_outline.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';
import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/16px/icon_16px_info_outline.svg';

const InputWrapper = forwardRef(function InputWrapper(props, ref) {
  return (
    <div ref={ref} className='relative w-full'>
      {props.children}
    </div>
  );
});

const StyledLocationIcon = ({ $state, className }) => {
  const iconClasses = classNames(className, {
    'text-icon-primary': $state === 'active',
    'text-icon-placeholder': $state === 'focused',
    'text-icon-secondary': $state === 'default',
  });
  return <LocationIcon className={iconClasses} />;
};

const StyledCalendarIcon = ({ $state, className }) => {
  const iconClasses = classNames(className, {
    'text-icon-primary': $state === 'active',
    'text-icon-placeholder': $state === 'focused',
    'text-icon-secondary': $state === 'default',
  });
  return <CalendarIcon className={iconClasses} />;
};

const getInputState = (focused, clicked) => {
  if (clicked) {
    return 'active';
  }
  if (focused) {
    return 'focused';
  }
  return 'default';
};

const getIconState = (value, fieldState) => {
  if (value instanceof Date || value) return 'active';
  if (fieldState === 'active' || fieldState === 'focused') return 'focused';
  return 'default';
};

const FIELD_INPUT_ID = {
  name: 'first-name-input',
  location: 'location-input',
  birthday: 'birthday-input',
  gender: 'gender-input',
  koreanSkills: 'korean-skills-input',
  nationality: 'nationality-input',
  careerRole: 'career-role-input',
  careerLevel: 'career-level-input',
  visaInfo: 'visa-info-input',
};

const PersonalInfoForm = ({ onSave, focusField, onBack }) => {
  const { user } = useAuth();
  const initialized = useRef(false);
  const [showTopikInput, setShowTopikInput] = useState(false);

  const [newUserData, setUserData] = useState({
    firstName: '',
    lastName: '',
    location: '',
    country: '',
    city: '',
    birthday: null,
    gender: '',
    koreanSkills: '',
    topikInfo: null,
    nationality: '',
    careerRole: '',
    careerLevel: '',
    visaInfo: {
      type: '',
      expiryDate: null,
    },
  });

  const [visibility, setVisibility] = useState({
    location: 'PUBLIC',
    birthday: 'PUBLIC',
    gender: 'PUBLIC',
    koreanSkills: 'PUBLIC',
    nationality: 'PUBLIC',
    careerRole: 'PUBLIC',
    careerLevel: 'PUBLIC',
    visaInfo: 'PUBLIC',
  });

  useEffect(() => {
    if (user && !initialized.current) {
      setUserData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        location: user.location || '',
        country: user.country || '',
        city: user.city || '',
        birthday:
          user.birthday && !isNaN(new Date(user.birthday))
            ? new Date(user.birthday)
            : null,
        gender: user.gender && user.gender !== 'null' ? user.gender : '',
        koreanSkills: user.koreanSkills || '',
        topikInfo:
          user.topikLevel || user.topikDate
            ? {
                type: 'topik',
                level: user.topikLevel || '',
                date: user.topikDate || '',
              }
            : null,
        nationality: user.nationality || '',
        careerRole: user.careerRole || '',
        careerLevel: user.careerLevel || '',
        visaInfo: user.visaInfo || {
          type: '',
          expiryDate: null,
        },
      });

      if (user.topikLevel || user.topikDate) {
        setShowTopikInput(true);
      }

      if (user.visibility) {
        setVisibility({
          location: user.visibility.location || 'PUBLIC',
          birthday: user.visibility.birthday || 'PUBLIC',
          gender: user.visibility.gender || 'PUBLIC',
          koreanSkills: user.visibility.koreanSkills || 'PUBLIC',
          nationality: user.visibility.nationality || 'PUBLIC',
          careerRole: user.visibility.careerRole || 'PUBLIC',
          careerLevel: user.visibility.careerLevel || 'PUBLIC',
          visaInfo: user.visibility.visaInfo || 'PUBLIC',
        });
      }

      initialized.current = true;
    }
  }, [user]);

  useEffect(() => {
    if (
      newUserData.koreanSkills?.startsWith('TOPIK') &&
      !newUserData.topikInfo
    ) {
      setUserData((prev) => ({
        ...prev,
        topikInfo: {
          type: prev.koreanSkills.includes('Speaking')
            ? 'topikSpeaking'
            : 'topik',
          level: '',
          date: '',
        },
      }));
      setShowTopikInput(true);
    }
  }, [newUserData.koreanSkills, newUserData.topikInfo]);

  useEffect(() => {
    if (!focusField) return;
    const id = FIELD_INPUT_ID[focusField];
    if (!id) return;
    requestAnimationFrame(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [focusField]);

  const [profileImageFile, setProfileImageFile] = useState(null);
  const [locationResults, setLocationResults] = useState([]);
  const [isCustomGender, setIsCustomGender] = useState(false);

  const [errors, setErrors] = useState({});

  const FORM_FIELDS = [
    'location',
    'birthday',
    'gender',
    'koreanSkills',
    'topik',
    'topikSpeaking',
    'nationality',
    'careerRole',
    'careerLevel',
    'visaInfo',
  ];

  const createFieldState = () =>
    FORM_FIELDS.reduce((acc, field) => {
      acc[field] = false;
      return acc;
    }, {});

  const [focus, setFocus] = useState(createFieldState);
  const [open, setOpen] = useState(createFieldState);

  const [topikDateFocused, setTopikDateFocused] = useState(false);
  const [topikSpeakingDateFocused, setTopikSpeakingDateFocused] =
    useState(false);

  const effectiveLocationStatus = getInputState(focus.location, false);
  const locationIconState = getIconState(
    newUserData.location,
    effectiveLocationStatus,
  );
  const effectiveBirthdayStatus = getInputState(focus.birthday, open.birthday);
  const birthdayIconState = getIconState(
    newUserData.birthday,
    effectiveBirthdayStatus,
  );
  const effectiveGenderStatus = getInputState(focus.gender, open.gender);
  const effectiveTopikInfoStatus = getInputState(
    focus.topikInfo,
    open.topik || open.topikSpeaking,
  );
  const effectiveKoreanSkillsStatus = getInputState(
    focus.koreanSkills,
    open.koreanSkills,
  );
  const effectiveTopikDateStatus = getInputState(false, topikDateFocused);
  const effectiveTopikSpeakingDateStatus = getInputState(
    false,
    topikSpeakingDateFocused,
  );
  const effectiveNationalityStatus = getInputState(
    focus.nationality,
    open.nationality,
  );
  const effectiveCareerStatus = getInputState(
    focus.careerLevel,
    open.careerLevel,
  );
  const effectiveVisaTypeStatus = getInputState(focus.visaInfo, open.visaInfo);
  const visaExpiryIconState = getIconState(
    newUserData.visaInfo?.expiryDate,
    effectiveVisaTypeStatus,
  );

  const handleFocus = (field) =>
    setFocus((prev) => ({ ...prev, [field]: true }));

  const handleBlur = (field) =>
    setFocus((prev) => ({ ...prev, [field]: false }));

  const toggleOpen = (field) =>
    setOpen((prev) => ({ ...prev, [field]: !prev[field] }));

  const closeOpen = (field) => setOpen((prev) => ({ ...prev, [field]: false }));

  const updateProfile = (field, value) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLocationSelect = (item) => {
    const newLocation = `${item.name}${
      item.country ? `, ${item.country}` : ''
    }`;
    setUserData((prev) => ({
      ...prev,
      location: newLocation,
      country: item.country || '',
      city: item.city || item.name || '',
    }));
    closeOpen('location');
    handleBlur('location');
  };

  const handleManualLocationEntry = (currentQuery) => {
    const [manualCity = '', ...manualCountryParts] = String(currentQuery)
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    setUserData((prev) => ({
      ...prev,
      location: currentQuery,
      country: manualCountryParts.join(', '),
      city: manualCity,
    }));
    setLocationResults([]);
    closeOpen('location');
    handleBlur('location');
  };

  const locationField = useLocationSelection({
    value: newUserData.location,
    onChange: (e) =>
      setUserData((prev) => ({
        ...prev,
        location: e.target.value,
        country: '',
        city: '',
      })),
    onFocus: () => handleFocus('location'),
    onBlur: () => handleBlur('location'),
    onSelect: handleLocationSelect,
    onManualEnter: handleManualLocationEntry,
  });

  useEffect(() => {
    const validate = () => {
      const newErrors = {};

      if (!newUserData.firstName.trim()) {
        newErrors.firstName = 'Please enter your first name.';
      }

      if (!newUserData.location.trim()) {
        newErrors.location = 'Please select your location.';
      }

      setErrors(newErrors);

      return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
      if (!validate()) return;
      if (!onSave) return;

      const [fallbackCity = '', ...fallbackCountryParts] = String(
        newUserData.location,
      )
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      const derivedCity = newUserData.city || fallbackCity;
      const derivedCountry =
        newUserData.country || fallbackCountryParts.join(', ');

      const payload = {
        profileImage: profileImageFile,
        firstName: newUserData.firstName,
        lastName: newUserData.lastName,
        location: newUserData.location,
        country: derivedCountry,
        city: derivedCity,
        birthday: newUserData.birthday
          ? format(newUserData.birthday, 'yyyy-MM-dd')
          : null,
        gender: newUserData.gender,
        koreanSkills: newUserData.koreanSkills,
        topikInfo: newUserData.topikInfo
          ? {
              level: newUserData.topikInfo.level,
              date: newUserData.topikInfo.date || null,
            }
          : null,
        nationality: newUserData.nationality,
        careerRole: newUserData.careerRole,
        careerLevel: newUserData.careerLevel,
        visaInfo: {
          type: newUserData.visaInfo?.type,
          expiryDate: newUserData.visaInfo?.expiryDate
            ? format(newUserData.visaInfo.expiryDate, 'yyyy-MM-dd')
            : null,
        },
        visibility: {
          location: visibility.location.toUpperCase(),
          birthday: visibility.birthday.toUpperCase(),
          gender: visibility.gender.toUpperCase(),
          koreanSkills: visibility.koreanSkills.toUpperCase(),
          nationality: visibility.nationality.toUpperCase(),
          careerRole: visibility.careerRole.toUpperCase(),
          careerLevel: visibility.careerLevel.toUpperCase(),
          visaInfo: visibility.visaInfo.toUpperCase(),
        },
      };
      onSave(payload);
    };

    document.addEventListener('saveProfile', handleSave);

    return () => {
      document.removeEventListener('saveProfile', handleSave);
    };
  }, [newUserData, visibility, onSave, profileImageFile]);

  const dropdownRefs = useRef({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.entries(dropdownRefs.current).forEach(([key, ref]) => {
        if (ref && !ref.contains(event.target)) {
          closeOpen(key);
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  return (
    <div className='flex flex-col items-start gap-6 tabletH:max-w-[568px] w-full pb-[40px]'>
      <PersonalInfoProfileImage onImageChange={setProfileImageFile} />

      <div className='flex justify-between gap-4 w-full'>
        {/* First Name */}
        <div className='flex-1 w-[calc(50%-8px)] relative'>
          <InputWrapper>
            <Input
              id='first-name-input'
              label='First name*'
              value={newUserData.firstName}
              hideCount
              onChange={(e) => updateProfile('firstName', e.target.value)}
              placeholder='First name'
              className='placeholder:opacity-0'
              state={errors.firstName && 'failed'}
              message={errors.firstName && errors.firstName}
            />
          </InputWrapper>
        </div>

        {/* Last Name */}
        <div className='flex-1 w-[calc(50%-8px)] relative'>
          <InputWrapper>
            <Input
              id='last-name-input'
              label='Last name'
              value={newUserData.lastName}
              hideCount
              showStatusIcon={false}
              onChange={(e) => updateProfile('lastName', e.target.value)}
              placeholder='Last name'
              className='placeholder:opacity-0'
            />
          </InputWrapper>
        </div>
      </div>

      {/* Location */}
      <InputWrapper>
        <VisibilityRadioGroup
          value={visibility.location}
          onChange={(val) =>
            setVisibility((prev) => ({
              ...prev,
              location: val,
            }))
          }
          disabled={!newUserData.location}
        />

        <Input
          id='location-input'
          ref={(el) => (dropdownRefs.current.location = el)}
          type='text'
          label='Location *'
          placeholder='Type your city or country'
          value={locationField.displayValue}
          onChange={locationField.handleChange}
          onFocus={locationField.handleFocus}
          onBlur={locationField.handleBlur}
          hideCount
          state={errors.location && 'failed'}
          message={errors.location && errors.location}
          forceActiveBorder={effectiveLocationStatus !== 'default'}
          leftIcon={<StyledLocationIcon $state={locationIconState} />}
          rightIcon={
            focus.location && locationField.displayValue ? (
              <button
                onMouseDown={locationField.handleClear}
                type='button'
                className='cursor-pointer flex items-center justify-center w-5 h-5 bg-grayscale-gray01 border-none rounded-[30px] hover:opacity-80 transition-opacity'
              >
                <CancelIcon className='w-4 h-4' />
              </button>
            ) : null
          }
        />

        {/* Location List */}
        {focus.location && locationField.displayValue.length >= 2 && (
          <div className='absolute top-[86px] left-0 right-0 z-10 bottom-[env(safe-area-inset-bottom)] max-h-[calc(100vh-60px)]'>
            <LocationAutoCompleteList
              searchText={locationField.displayValue}
              results={locationResults}
              onSelect={locationField.handleSelect}
              onManualEnter={locationField.handleManualEnter}
              isMyPage={true}
            />
          </div>
        )}
      </InputWrapper>

      {/* Birthday */}
      <InputWrapper>
        <VisibilityRadioGroup
          value={visibility.birthday}
          onChange={(val) =>
            setVisibility((prev) => ({
              ...prev,
              birthday: val,
            }))
          }
          disabled={!newUserData.birthday}
        />

        <Input
          id='birthday-input'
          label='Birthday'
          placeholder='Select your birthday'
          value={
            newUserData.birthday
              ? format(newUserData.birthday, 'dd MMM yyyy')
              : ''
          }
          state={effectiveBirthdayStatus}
          readOnly
          onClick={() => toggleOpen('birthday')}
          onChange={(date) => updateProfile('birthday', date)}
          hideCount
          showStatusIcon={false}
          rightIcon={<StyledCalendarIcon $state={birthdayIconState} />}
          onRightIconClick={() => toggleOpen('birthday')}
        />

        {open.birthday && (
          <div
            className='absolute top-[26px] left-0 z-10'
            onClick={(e) => e.stopPropagation()}
          >
            <CustomDatePicker
              selectedDate={newUserData.birthday}
              onChange={(date) => {
                updateProfile('birthday', date);
                closeOpen('birthday');
                handleBlur('birthday');
              }}
              onClose={() => {
                closeOpen('birthday');
                handleBlur('birthday');
              }}
              onClickOutside={() => {
                closeOpen('birthday');
                handleBlur('birthday');
              }}
            />
          </div>
        )}
      </InputWrapper>

      {/* Gender */}
      <InputWrapper>
        <VisibilityRadioGroup
          value={visibility.gender}
          onChange={(val) =>
            setVisibility((prev) => ({
              ...prev,
              gender: val,
            }))
          }
          disabled={!newUserData.gender}
        />
        <Input
          id='gender-input'
          ref={(el) => (dropdownRefs.current.gender = el)}
          label='Gender'
          placeholder={
            !isCustomGender ? 'Select your gender' : 'What’s your gender?'
          }
          value={newUserData.gender}
          state={effectiveGenderStatus}
          readOnly={!isCustomGender}
          onClick={() => toggleOpen('gender')}
          onChange={(e) => updateProfile('gender', e.target.value)}
          onFocus={() => handleFocus('gender')}
          onBlur={() => {
            handleBlur('gender');
            closeOpen('gender');
          }}
          hideCount
          showStatusIcon={false}
          rightIcon={
            open.gender ? (
              <ArrowTopIcon className='transition-transform duration-300 w-5 h-5' />
            ) : (
              <ArrowBottomIcon className='transition-transform duration-300 w-5 h-5' />
            )
          }
          onRightIconClick={() => toggleOpen('gender')}
        />

        {/* Gender List */}
        {open.gender && (
          <div className='absolute top-[84px] left-0 z-10 gender-list-wrapper w-full'>
            <GenderList
              onSelect={(selectedGender) => {
                if (selectedGender === 'Custom') {
                  setIsCustomGender(true);
                  setUserData((prev) => ({
                    ...prev,
                    gender: '',
                  }));
                  closeOpen('gender');

                  requestAnimationFrame(() => {
                    dropdownRefs.current.gender.current?.focus();
                  });
                  return;
                }
                setUserData((prev) => ({
                  ...prev,
                  gender: selectedGender,
                }));
                setIsCustomGender(false);
                closeOpen('gender');
                handleBlur('gender');
              }}
            />
          </div>
        )}
      </InputWrapper>

      {/* Korean skills */}
      <InputWrapper>
        <VisibilityRadioGroup
          value={visibility.koreanSkills}
          onChange={(val) =>
            setVisibility((prev) => ({
              ...prev,
              koreanSkills: val,
            }))
          }
          disabled={!newUserData.koreanSkills}
        />
        <Input
          id='korean-skills-input'
          ref={(el) => (dropdownRefs.current.koreanSkills = el)}
          label='Korean Skills'
          placeholder='Select your korean level'
          value={newUserData.koreanSkills}
          state={effectiveKoreanSkillsStatus}
          readOnly
          onClick={() => toggleOpen('koreanSkills')}
          onFocus={() => handleFocus('koreanSkills')}
          onBlur={() => {
            handleBlur('koreanSkills');
            closeOpen('koreanSkills');
          }}
          hideCount
          showStatusIcon={false}
          rightIcon={
            open.koreanSkills ? (
              <ArrowTopIcon className='transition-transform duration-300 w-5 h-5' />
            ) : (
              <ArrowBottomIcon className='transition-transform duration-300 w-5 h-5' />
            )
          }
          onRightIconClick={() => toggleOpen('koreanSkills')}
        />

        {open.koreanSkills && (
          <div className='absolute top-[84px] left-0 z-10 w-full'>
            <KoreanSkillsList
              onSelect={(selected) => {
                setUserData((prev) => {
                  let newTopikInfo = null;

                  if (selected === 'TOPIK certified') {
                    newTopikInfo = {
                      type: 'topik',
                      level: '',
                      date: '',
                    };
                  } else if (selected === 'TOPIK Speaking certified') {
                    newTopikInfo = {
                      type: 'topikSpeaking',
                      level: '',
                      date: '',
                    };
                  }

                  return {
                    ...prev,
                    koreanSkills: selected,
                    topikInfo: newTopikInfo,
                  };
                });

                closeOpen('koreanSkills');
                handleBlur('koreanSkills');
              }}
            />
          </div>
        )}

        {newUserData.topikInfo && (
          <div className='w-full mt-2'>
            <div
              className='group flex flex-col gap-4 px-4 py-3 items-start rounded-lg
                   bg-grayscale-gray02/40 border-grayscale-gray02 border
                   hover:bg-grayscale-gray02/70'
            >
              <button
                type='button'
                onClick={() => setShowTopikInput((prev) => !prev)}
                className='flex items-center gap-2 typo-b14Strong text-text-placeholder w-full'
              >
                <span className='flex w-5 h-5 justify-center items-center bg-grayscale-gray03/40 group-hover:bg-grayscale-gray03/60 rounded-[10px]'>
                  {showTopikInput ? (
                    <ArrowTopIcon className='w-4 h-4 text-icon-placeholder' />
                  ) : (
                    <ArrowBottomIcon className='w-4 h-4 text-icon-placeholder' />
                  )}
                </span>
                Add certification
              </button>

              {showTopikInput && (
                <div className='flex flex-col gap-2'>
                  <div
                    className='grid
                      grid-cols-[340px_180px]
                      gap-4 w-full'
                  >
                    <div
                      ref={(el) =>
                        (dropdownRefs.current[
                          newUserData.topikInfo?.type === 'topik'
                            ? 'topik'
                            : 'topikSpeaking'
                        ] = el)
                      }
                      className='relative w-full'
                    >
                      <div className='flex flex-col gap-1'>
                        <div className='typo-b12Strong text-text-placeholder'>
                          {newUserData.topikInfo?.type === 'topik'
                            ? 'TOPIK Level'
                            : 'TOPIK speaking Level'}
                        </div>
                        <Input
                          label=''
                          placeholder='Select level'
                          value={newUserData.topikInfo?.level}
                          readOnly
                          state={effectiveTopikInfoStatus}
                          rightIcon={
                            (
                              newUserData.topikInfo?.type === 'topik'
                                ? open.topik
                                : open.topikSpeaking
                            ) ? (
                              <ArrowTopIcon className='transition-transform duration-300 w-5 h-5' />
                            ) : (
                              <ArrowBottomIcon className='transition-transform duration-300 w-5 h-5' />
                            )
                          }
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={
                            newUserData.topikInfo?.type === 'topik'
                              ? (e) => {
                                  e.stopPropagation();
                                  toggleOpen('topik');
                                }
                              : (e) => {
                                  e.stopPropagation();
                                  toggleOpen('topikSpeaking');
                                }
                          }
                          onRightIconClick={
                            newUserData.topikInfo?.type === 'topik'
                              ? (e) => {
                                  e.stopPropagation();
                                  toggleOpen('topik');
                                }
                              : (e) => {
                                  e.stopPropagation();
                                  toggleOpen('topikSpeaking');
                                }
                          }
                        />

                        {newUserData.topikInfo?.type === 'topik' &&
                          open.topik && (
                            <div className='absolute top-[74px] left-0 z-10'>
                              <TopikList
                                selectedOption={newUserData.topikInfo?.level}
                                onSelect={(option) => {
                                  updateProfile('topikInfo', {
                                    ...newUserData.topikInfo,
                                    level: option,
                                  });
                                  closeOpen('topik');
                                  handleBlur('topik');
                                }}
                                isMyPage={true}
                              />
                            </div>
                          )}

                        {newUserData.topikInfo?.type === 'topikSpeaking' &&
                          open.topikSpeaking && (
                            <div className='absolute top-[74px] left-0 z-10'>
                              <TopikSpeakingList
                                selectedOption={newUserData.topikInfo?.level}
                                onSelect={(option) => {
                                  updateProfile('topikInfo', {
                                    ...newUserData.topikInfo,
                                    level: option,
                                  });
                                  closeOpen('topikSpeaking');
                                  handleBlur('topikSpeaking');
                                }}
                                isMyPage={true}
                              />
                            </div>
                          )}
                      </div>
                    </div>

                    <InputWrapper>
                      <div className='flex flex-col gap-1'>
                        <div className='typo-b12Strong text-text-placeholder'>
                          Issue date
                        </div>
                        <Input
                          id='topik-date-input'
                          label=''
                          value={newUserData.topikInfo?.date}
                          placeholder='YYYY / MM / DD'
                          state={
                            newUserData.topikInfo?.type === 'topik'
                              ? effectiveTopikDateStatus
                              : effectiveTopikSpeakingDateStatus
                          }
                          hideCount
                          showStatusIcon={false}
                          onChange={(e) =>
                            updateProfile('topikInfo', {
                              ...newUserData.topikInfo,
                              date: e.target.value,
                            })
                          }
                          onFocus={() =>
                            newUserData.topikInfo?.type === 'topik'
                              ? setTopikDateFocused(true)
                              : setTopikSpeakingDateFocused(true)
                          }
                          onBlur={() =>
                            newUserData.topikInfo?.type === 'topik'
                              ? setTopikDateFocused(false)
                              : setTopikSpeakingDateFocused(false)
                          }
                        />
                      </div>
                    </InputWrapper>
                  </div>
                  <div className='flex items-center gap-0.5'>
                    <InfoIcon className='text-grayscale-gray03' />
                    <div className='typo-b13 text-grayscale-gray04'>
                      This information will be used in your resume.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </InputWrapper>

      {/* Nationality */}
      <NationalityInputBlock
        id='nationality-input'
        value={newUserData.nationality}
        inputRef={(el) => (dropdownRefs.current.nationality = el)}
        state={focus.nationality ? 'active' : effectiveNationalityStatus}
        focused={focus.nationality}
        onChange={(e) => updateProfile('nationality', e.target.value)}
        onSelect={(val) => {
          updateProfile('nationality', val);
          handleBlur('nationality');
        }}
        onFocus={() => handleFocus('nationality')}
        onBlur={() => handleBlur('nationality')}
        isMyPage={true}
        visibility={visibility.nationality}
        onVisibilityChange={(val) =>
          setVisibility((prev) => ({
            ...prev,
            nationality: val,
          }))
        }
        disabled={!newUserData.nationality}
      />

      {/* Role */}
      <div className='flex flex-col gap-2 w-full'>
        <InputWrapper ref={(el) => (dropdownRefs.current.careerRole = el)}>
          <VisibilityRadioGroup
            value={visibility.careerRole}
            onChange={(val) =>
              setVisibility((prev) => ({
                ...prev,
                careerRole: val,
              }))
            }
            disabled={!newUserData.careerRole}
          />
          <Input
            label='Role'
            id='career-role-input'
            placeholder='Select your role'
            value={newUserData.careerRole}
            showStatusIcon={false}
            readOnly
            rightIcon={
              open.careerRole ? (
                <ArrowTopIcon className='transition-transform duration-300 w-5 h-5' />
              ) : (
                <ArrowBottomIcon className='transition-transform duration-300 w-5 h-5' />
              )
            }
            onClick={() => toggleOpen('careerRole')}
          />

          {open.careerRole && (
            <div className='absolute left-0 z-20 w-full mt-1 top-full'>
              <RoleList
                selectedOption={newUserData.careerRole}
                onSelect={(option) => {
                  updateProfile('careerRole', option);
                  closeOpen('careerRole');
                  handleBlur('careerRole');
                }}
              />
            </div>
          )}
        </InputWrapper>

        {newUserData.careerRole === 'Other (Enter manually)' && (
          <Input
            label=''
            placeholder='Enter role title (e.g. Legal Counsel)'
            value={newUserData.customRole || ''}
            showStatusIcon={false}
            onChange={(e) => updateProfile('customRole', e.target.value)}
          />
        )}
      </div>

      {/* Career Level */}
      <InputWrapper ref={(el) => (dropdownRefs.current.careerLevel = el)}>
        <VisibilityRadioGroup
          value={visibility.careerLevel}
          onChange={(val) =>
            setVisibility((prev) => ({
              ...prev,
              careerLevel: val,
            }))
          }
          disabled={!newUserData.careerLevel}
        />
        <Input
          label='Career level'
          id='career-level-input'
          placeholder='Select your career level'
          value={newUserData.careerLevel}
          state={effectiveCareerStatus}
          readOnly
          showStatusIcon={false}
          rightIcon={
            open.careerLevel ? (
              <ArrowTopIcon className='transition-transform duration-300 w-5 h-5' />
            ) : (
              <ArrowBottomIcon className='transition-transform duration-300 w-5 h-5' />
            )
          }
          onClick={() => toggleOpen('careerLevel')}
          onRightIconClick={() => toggleOpen('careerLevel')}
          onFocus={() => handleFocus('careerLevel')}
          onBlur={() => handleBlur('careerLevel')}
        />

        {/* Career List */}
        {open.careerLevel && (
          <div className='absolute top-[84px] left-0 z-10 w-full'>
            <CareerList
              selectedOption={newUserData.careerLevel || 'Select Career level'}
              onSelect={(option) => {
                updateProfile('careerLevel', option);
                closeOpen('careerLevel');
                handleBlur('careerLevel');
              }}
            />
          </div>
        )}
      </InputWrapper>

      {/* Visa Info */}
      {newUserData.visaInfo && (
        <div
          className='
            grid grid-cols-1
            tabletH:grid-cols-[360px_200px]
            gap-2 items-end w-full
        '
        >
          {/* Visa Type */}
          <InputWrapper ref={(el) => (dropdownRefs.current.visaInfo = el)}>
            <Input
              label='Visa info'
              id='visa-info-input'
              placeholder='Select your visa type'
              value={newUserData.visaInfo?.type}
              readOnly
              showStatusIcon={false}
              state={effectiveVisaTypeStatus}
              rightIcon={
                open.visaInfo ? (
                  <ArrowTopIcon className='transition-transform duration-300 w-5 h-5' />
                ) : (
                  <ArrowBottomIcon className='transition-transform duration-300 w-5 h-5' />
                )
              }
              onClick={() => toggleOpen('visaInfo')}
              onRightIconClick={() => toggleOpen('visaInfo')}
            />

            {open.visaInfo && (
              <div className='absolute top-[84px] left-0 z-10 w-full'>
                <VisaTypeList
                  selectedOption={newUserData.visaInfo?.type}
                  onSelect={(value) => {
                    updateProfile('visaInfo', {
                      ...newUserData.visaInfo,
                      type: value,
                    });
                    closeOpen('visaInfo');
                  }}
                  onClose={() => closeOpen('visaInfo')}
                />
              </div>
            )}
          </InputWrapper>

          {/* Expiry Date */}
          <InputWrapper>
            <div className='flex justify-end mb-2'>
              <VisibilityRadioGroup
                isVisaInfo
                value={visibility.visaInfo}
                onChange={(val) =>
                  setVisibility((prev) => ({
                    ...prev,
                    visaInfo: val,
                  }))
                }
                disabled={!newUserData.visaInfo?.type}
              />
            </div>

            <Input
              label=''
              placeholder='Select expiry date'
              value={
                newUserData.visaInfo?.expiryDate
                  ? format(newUserData.visaInfo.expiryDate, 'dd MMM yyyy')
                  : ''
              }
              readOnly
              showStatusIcon={false}
              rightIcon={<StyledCalendarIcon $state={visaExpiryIconState} />}
              onClick={() => toggleOpen('visaExpiry')}
              onRightIconClick={() => toggleOpen('visaExpiry')}
            />

            {open.visaExpiry && (
              <div className='absolute top-8 left-0 z-10 w-full'>
                <CustomDatePicker
                  isVisaExpiryDate
                  isMyPage
                  selectedDate={newUserData.visaInfo?.expiryDate}
                  onChange={(date) => {
                    updateProfile('visaInfo', {
                      ...newUserData.visaInfo,
                      expiryDate: date,
                    });
                    closeOpen('visaExpiry');
                  }}
                  onClose={() => {
                    closeOpen('visaExpiry');
                    handleBlur('visaExpiry');
                  }}
                  onClickOutside={() => {
                    closeOpen('visaExpiry');
                    handleBlur('visaExpiry');
                  }}
                />
              </div>
            )}
          </InputWrapper>
        </div>
      )}
    </div>
  );
};

export default PersonalInfoForm;
